// Branded lab-report template modelled after the Dekyi Medical sample.
//
//   • Blue header band with logo + facility info + a Code-128 barcode that
//     encodes the patient/order identifier so a scanner can pull up results.
//   • Patient/order block split into two columns under the header.
//   • Per-category result tables (Test / Result / Unit / Ref. Range / Status).
//   • End-of-report rule and a 3-row signature block (Tested / Verified /
//     Approved). Tested-by is auto-filled from the logged-in user; verified
//     and approved are collected in the print dialog before the popup opens.
//
// The popup window loads JsBarcode from a CDN to render the barcode at print
// time — no npm install required. If the CDN is unreachable the section
// gracefully degrades to a text representation of the barcode value.

const escapeHtml = (value) => {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const formatStatus = (flag) => {
  if (!flag) return 'NORMAL';
  const upper = String(flag).toUpperCase();
  if (upper === 'NORMAL' || upper === 'N' || upper === '-') return 'NORMAL';
  if (upper === 'HIGH' || upper === 'H') return 'HIGH';
  if (upper === 'LOW'  || upper === 'L') return 'LOW';
  if (upper === 'CRITICAL' || upper === 'C') return 'CRITICAL';
  return upper;
};

const statusClass = (status) => {
  const s = status.toUpperCase();
  if (s === 'NORMAL') return 'status-normal';
  if (s === 'HIGH' || s === 'CRITICAL') return 'status-high';
  if (s === 'LOW') return 'status-low';
  return 'status-other';
};

// Direct hex lookup — used inline on the status cell, chart marker and
// chart value label. The class-based rules below are kept for older
// browsers, but inline styles + print-color-adjust survive every
// browser's "strip colours on print" behaviour, which CSS classes alone
// don't reliably do. NORMAL = green, LOW = amber, HIGH/CRITICAL = red.
const statusColor = (status) => {
  const s = String(status).toUpperCase();
  if (s === 'NORMAL') return '#16a34a';
  if (s === 'HIGH' || s === 'CRITICAL') return '#dc2626';
  if (s === 'LOW') return '#d97706';
  return '#4b5563';
};

// One-liner used everywhere we set a colour we don't want the browser
// to override at print time.
const colorStyle = (hex) =>
  `color:${hex} !important;-webkit-print-color-adjust:exact;print-color-adjust:exact;color-adjust:exact;`;

// ── Reference-range parser ────────────────────────────────────────────────
// Real-world range strings come in a handful of shapes:
//   "2.0-7.0"   "0.5 - 1.7"   "<3.0"   ">1.0"   "3.5-5.0"   "≤200"   "≥40"
// Returns { lo, hi } as numbers, with either side `null` when open-ended.
// Returns null if the range can't be parsed numerically (qualitative, etc).
const parseRange = (rangeStr) => {
  if (rangeStr === null || rangeStr === undefined) return null;
  const s = String(rangeStr).trim();
  if (!s || s === '-') return null;
  // <3.0 or ≤3.0
  let m = s.match(/^[<≤]\s*(-?\d*\.?\d+)/);
  if (m) return { lo: null, hi: parseFloat(m[1]) };
  // >1.0 or ≥1.0
  m = s.match(/^[>≥]\s*(-?\d*\.?\d+)/);
  if (m) return { lo: parseFloat(m[1]), hi: null };
  // 2.0-7.0  /  2.0 to 7.0
  m = s.match(/^(-?\d*\.?\d+)\s*(?:-|to|–|—)\s*(-?\d*\.?\d+)/i);
  if (m) {
    const lo = parseFloat(m[1]);
    const hi = parseFloat(m[2]);
    if (Number.isFinite(lo) && Number.isFinite(hi)) return { lo, hi };
  }
  return null;
};

// ── Per-row chart renderer ────────────────────────────────────────────────
// Builds the inline HTML for the "Chart" column: a horizontal bar with the
// reference-range zone shaded green and a triangle marker showing the
// patient's value. Colour of the marker reflects the status flag.
//
// Output is a static HTML snippet — no client-side JS — so it prints
// identically to the screen rendering.
const buildChartCell = (resultStr, range, status) => {
  const num = parseFloat(String(resultStr ?? '').replace(/[^0-9.\-]/g, ''));
  if (!Number.isFinite(num) || !range) return '<span class="chart-na">—</span>';
  const { lo, hi } = range;
  if (lo === null && hi === null) return '<span class="chart-na">—</span>';

  // Establish a domain that contains both the range and the value with a
  // comfortable margin so the marker is never glued to an edge.
  const refLo = lo !== null ? lo : Math.min(0, num);
  const refHi = hi !== null ? hi : Math.max(num, refLo + Math.abs(refLo || 1));
  const span  = Math.max(refHi - refLo, Math.abs(refHi || 1) || 1);
  const pad   = span * 0.4;
  let dMin = Math.min(refLo - pad, num - pad * 0.3);
  let dMax = Math.max(refHi + pad, num + pad * 0.3);
  if (dMax - dMin <= 0) { dMax = dMin + 1; }

  const pct = (v) => ((v - dMin) / (dMax - dMin)) * 100;
  const normLeft  = lo !== null ? Math.max(0, pct(refLo))  : 0;
  const normRight = hi !== null ? Math.min(100, pct(refHi)) : 100;
  const normW     = Math.max(0, normRight - normLeft);
  const markerLeft = Math.max(0, Math.min(100, pct(num)));

  const cls   = statusClass(status);
  const cInline = colorStyle(statusColor(status));
  // Render the value tick label only when it doesn't collide with the bar
  // edges — keeps things readable at low domain widths.
  return `
    <div class="chart-cell">
      <div class="chart-bar">
        <div class="chart-zone" style="left:${normLeft.toFixed(1)}%;width:${normW.toFixed(1)}%;"></div>
        <div class="chart-marker ${cls}" style="left:${markerLeft.toFixed(1)}%;${cInline}" title="${escapeHtml(String(num))}">▲</div>
        <div class="chart-val ${cls}" style="left:${markerLeft.toFixed(1)}%;${cInline}">${escapeHtml(String(num))}</div>
      </div>
    </div>
  `;
};

export const generateLabReportHTMLDekyi = (
  reportData,
  selectedPatient,
  userInfo,
  companyInfo,
  signatures = {},
  categoryInterpretations = {},
  categoryHistograms = {},
  categoryNarratives = {},
  // Per-category format hint from the catalog. Only categories
  // explicitly marked 'narrative' get the descriptive layout; everything
  // else (including labs with empty units / ranges) renders the standard
  // parameter table. Defaults to '{}' so older callers that don't pass
  // the map still render every lab as a parameter table.
  categoryFormats = {}
) => {
  // Pull cached assembly info from localStorage so the facility's phone,
  // email, and address always render — even when the caller forgot to pass
  // companyInfo. The companyInfo arg still wins when it carries values
  // (e.g. a logo URL), but missing fields fall back to the cached row.
  let assemblyCache = {};
  try {
    const raw = (typeof localStorage !== 'undefined') ? localStorage.getItem('companyInfo') : null;
    if (raw) assemblyCache = JSON.parse(raw) || {};
  } catch (_) { assemblyCache = {}; }

  const baseCompany = (companyInfo && companyInfo.length > 0)
    ? companyInfo[0]
    : {};
  const company = {
    name: 'Medical & Diagnostics Imaging Center',
    location: '',
    digital_add: '',
    phone: '',
    email: '',
    mmda: '',
    ...assemblyCache,
    ...Object.fromEntries(Object.entries(baseCompany).filter(([, v]) => v !== null && v !== undefined && v !== '')),
  };

  // Combined facility address: location + digital address, separated by a
  // bullet only when both exist. Used in the header band.
  const facilityAddress = [company.location, company.digital_add]
    .filter(v => v && String(v).trim() !== '')
    .join(' · ');

  const dateOfResult = Object.keys(reportData)[0] || '';
  const now = new Date();
  const registeredOn = dateOfResult || now.toLocaleDateString();
  const reportedOn   = now.toLocaleString();

  // Stable order number — uses an explicitly-passed order, falls back to a
  // deterministic combination so the barcode is still scannable in the
  // history view where no order id was generated.
  const orderNumber = signatures.orderNumber
    || selectedPatient?.order_number
    || selectedPatient?.path_no
    || `${selectedPatient?.id || 'X'}-${(dateOfResult || '').replace(/[^0-9]/g, '') || Date.now()}`;

  const barcodeValue = String(orderNumber || selectedPatient?.id || 'NO-ID').slice(0, 40);

  // Tested-by defaults to whoever is logged in; the verified/approved blocks
  // come from the dialog. Each block keeps its own date so back-dated
  // verifications stay accurate.
  const testedBy   = signatures.testedBy   || userInfo?.name || userInfo?.user_name || userInfo?.username || '';
  const verifiedBy = signatures.verifiedBy || '';
  const verifiedTitle = signatures.verifiedTitle || '';
  const approvedBy = signatures.approvedBy || '';
  const approvedTitle = signatures.approvedTitle || '';
  const todayStr   = now.toLocaleDateString();

  // One <table> per lab category, ordered by date desc then category name.
  // Tiny inline escaper — narrative paragraphs are tech-typed free text
  // so we have to assume they may contain &, <, > etc.
  const escHtml = (s) => String(s ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

  const labsHTML = Object.keys(reportData)
    .sort((a, b) => new Date(b) - new Date(a))
    .map((date) => Object.keys(reportData[date])
      .sort()
      .map((testName) => {
        const rows = reportData[date][testName];

        // Narrative lab detection — STRICTLY driven by the catalog's
        // lab_format flag. The previous version inferred narrative from
        // missing unit/range, which caught legacy parameter labs that
        // simply hadn't filled in those columns and silently flipped
        // them to the descriptive template. Only categories the admin
        // explicitly set to 'narrative' use this branch.
        //
        // Two sources, either is enough. The category map is the primary
        // one, but it only arrives if the calling screen forwarded it —
        // and several print paths didn't, so a narrative lab printed as a
        // numeric table with Unit and Status columns. The backend now also
        // stamps `lab_format` on every row, which travels with the data
        // itself and can't be dropped on the way to the template.
        const isNarrative = categoryFormats[testName] === 'narrative'
          || rows.some(r => r && r.lab_format === 'narrative');

        if (isNarrative) {
          const impressionText = (rows.find(r => r.impression && String(r.impression).trim()) || {}).impression || '';
          const sectionRows = rows
            .filter(r => r.parameter && String(r.parameter).trim())
            .map(r => `
              <tr>
                <td style="padding:8px 12px; border:1px solid #d9dee5; vertical-align:top; width:30%; font-weight:600;">${escHtml(r.parameter)}</td>
                <td style="padding:8px 12px; border:1px solid #d9dee5; white-space:pre-wrap;">${escHtml(r.result)}</td>
              </tr>
            `).join('');

          return `
            <div style="margin: 14px 16px;">
              <div style="text-align:center; font-weight:800; font-size:13px; letter-spacing:0.5px; text-transform:uppercase; margin: 8px 0;">
                ${escHtml(testName)}
              </div>
              <table style="width:100%; border-collapse:collapse; font-size:11.5px; margin-bottom: 10px;">
                <thead>
                  <tr style="background:#3a5fd9; color:white;">
                    <th style="padding:8px 12px; text-align:left; border:1px solid #2a4ec0;">Test Name</th>
                    <th style="padding:8px 12px; text-align:left; border:1px solid #2a4ec0;">Result</th>
                  </tr>
                </thead>
                <tbody>${sectionRows}</tbody>
              </table>
              ${impressionText ? `
                <div style="margin: 10px 0; padding: 6px 0; font-size: 11.5px;">
                  <span style="font-weight:700;">Impression:</span> ${escHtml(impressionText)}
                </div>
              ` : ''}
              <div style="text-align:center; font-size:11px; letter-spacing:1px; color:#555; margin:8px 0;">
                * * * * * End of Report * * * * *
              </div>
            </div>
          `;
        }

        const nonConclusion = rows.filter(r => !r.parameter || !r.parameter.toUpperCase().includes('CONCLUSION'));
        const conclusions   = rows.filter(r =>  r.parameter &&  r.parameter.toUpperCase().includes('CONCLUSION'));
        const ordered = [...nonConclusion, ...conclusions];

        // ── Dual-unit helpers (per-row) ──────────────────────────
        // Count the decimal places in the primary result so the converted
        // alt value and the converted alt range stay at the same precision.
        // "8.0" → 1, "8.05" → 2, "8" → 0 — never auto-clipped to 2/3 by
        // magnitude.
        const dpOf = (s) => {
          const t = String(s ?? '').trim();
          if (!t) return null;
          const m = t.match(/\.(\d+)$/);
          if (m) return m[1].length;
          if (/^-?\d+$/.test(t)) return 0;
          return null;
        };
        const matchDp = (n, srcStr) => {
          if (!Number.isFinite(n)) return '';
          const dp = dpOf(srcStr);
          if (dp != null) return n.toFixed(dp);
          const abs = Math.abs(n);
          if (abs >= 100) return n.toFixed(1);
          if (abs >= 1)   return n.toFixed(2);
          return n.toFixed(3);
        };
        // Derive the primary→alt factor from the saved row itself. When the
        // row has both numeric values and the primary is non-zero we can
        // recover the original `unit_factor` exactly; otherwise no factor —
        // the alt range simply isn't shown.
        const deriveFactor = (row) => {
          const a = parseFloat(row.result);
          const b = parseFloat(row.result_alt);
          if (!Number.isFinite(a) || !Number.isFinite(b) || a === 0) return null;
          return b / a;
        };
        // Convert a parsed range through the factor, formatting both sides
        // to the alt result's precision so the printed report reads cleanly.
        const buildAltRange = (parsedR, factor, altResultStr) => {
          if (!parsedR || factor == null || !Number.isFinite(factor)) return '';
          const lo = parsedR.lo != null ? matchDp(parsedR.lo * factor, altResultStr) : null;
          const hi = parsedR.hi != null ? matchDp(parsedR.hi * factor, altResultStr) : null;
          if (lo != null && hi != null) return `${lo}-${hi}`;
          if (hi != null) return `<${hi}`;
          if (lo != null) return `>${lo}`;
          return '';
        };

        // Decide once per section whether Ref. Range and Chart columns are
        // worth showing. If every row in the section has no meaningful range
        // (empty / "-" / "Not specified") the column is hidden. If no row
        // can produce a renderable chart bar (non-numeric result or
        // unparseable range) the chart column is also hidden.
        const showRange = ordered.some(r => {
          const s = (r.range || '').trim().toLowerCase();
          return s !== '' && s !== '-' && s !== 'not specified';
        });
        const showChart = ordered.some(r => {
          return parseRange(r.range) !== null && Number.isFinite(parseFloat(r.result));
        });

        const body = ordered.map((r) => {
          const status = formatStatus(r.flag);
          // For dual-unit parameters the layout becomes:
          //   Result cell  — primary value, then alt value beneath (no unit)
          //   Unit cell    — primary unit / alt unit
          //   Range cell   — primary range, then converted alt range beneath
          // Decimal precision of the alt value/range mirrors the primary so
          // a 1-dp primary doesn't end up next to a 3-dp converted neighbour.
          const hasAlt = r.result_alt && r.unit_alt;
          const factor = hasAlt ? deriveFactor(r) : null;
          const altResultDisplay = hasAlt && Number.isFinite(parseFloat(r.result_alt))
            ? matchDp(parseFloat(r.result_alt), r.result)
            : (r.result_alt || '');

          const resultCell = hasAlt
            ? `${escapeHtml(r.result || '-')}<br/><span style="color:#6b7280;font-size:8.4pt;">${escapeHtml(altResultDisplay)}</span>`
            : escapeHtml(r.result || '-');
          const unitCell = hasAlt
            ? `${escapeHtml(r.unit || '-')} / ${escapeHtml(r.unit_alt)}`
            : escapeHtml(r.unit || '-');

          const parsedRange = parseRange(r.range);
          const altRange = hasAlt ? buildAltRange(parsedRange, factor, altResultDisplay) : '';
          const rangeCell = altRange
            ? `${escapeHtml(r.range || '-')}<br/><span style="color:#6b7280;font-size:8.4pt;">${escapeHtml(altRange)}</span>`
            : escapeHtml(r.range || '-');

          const chartCell = buildChartCell(r.result, parsedRange, status);
          return `
            <tr>
              <td class="cell-test">${escapeHtml(r.parameter || '-')}</td>
              <td class="cell-result">${resultCell}</td>
              <td class="cell-unit">${unitCell}</td>
              ${showRange ? `<td class="cell-range">${rangeCell}</td>` : ''}
              <td class="cell-status ${statusClass(status)}" style="${colorStyle(statusColor(status))}font-weight:700;">${escapeHtml(status)}</td>
              ${showChart ? `<td class="cell-chart">${chartCell}</td>` : ''}
            </tr>
          `;
        }).join('');

        const interp = categoryInterpretations[testName] || '';
        const interpBlock = interp
          ? `<div class="interpretation-block">${interp}</div>`
          : '';

        // Distribution histograms from the analyzer (WBC/RBC/PLT
        // graphs). Only render when this category has any — keeps
        // non-CBC reports clean.
        const hists = Array.isArray(categoryHistograms[testName]) ? categoryHistograms[testName] : [];
        const histogramsBlock = hists.length > 0
          ? `<div class="histograms-block">
               <div class="histograms-title">Distribution Graphs</div>
               <div class="histograms-grid">
                 ${hists.map(h => `
                   <div class="histogram-card">
                     <div class="histogram-name">${escapeHtml((h.name || '').replace(/\.\s*(BMP|PNG|JPEG|GIF).*$/i, '').trim())}</div>
                     <img class="histogram-img" src="${h.dataUri}" alt="${escapeHtml(h.name || 'Histogram')}" />
                   </div>
                 `).join('')}
               </div>
             </div>`
          : '';

        // Analyzer-emitted narratives — "WBC Message", "RBC Message",
        // "PLT Message". Grouped by message category and rendered as
        // side-by-side columns so a row of WBC alerts (Leucocytosis,
        // Neutrophilia, …) doesn't pad the page with one tall list per
        // alert. Mirrors the analyzer's own UI layout.
        const narrs = Array.isArray(categoryNarratives[testName]) ? categoryNarratives[testName] : [];
        const narrativesBlock = (() => {
          if (narrs.length === 0) return '';
          const groups = new Map();
          for (const n of narrs) {
            const key = (n.name || 'Message').toString().trim();
            // Some analyzer firmwares pack multiple alerts into one row
            // ("Leucocytosis, Neutrophilia" or "Anemia~Microcytic").
            // Split so each alert prints on its own line in the column.
            const items = String(n.text || '')
              .split(/[,;~\r\n]+/)
              .map(s => s.trim())
              .filter(Boolean);
            if (items.length === 0) continue;
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(...items);
          }
          if (groups.size === 0) return '';
          const cols = Array.from(groups.entries())
            .map(([name, items]) => `
              <div class="narrative-col">
                <div class="narrative-col-title">${escapeHtml(name)}</div>
                <ul class="narrative-list">
                  ${items.map(it => `<li>${escapeHtml(it)}</li>`).join('')}
                </ul>
              </div>
            `).join('');
          return `<div class="narratives-block">
                    <div class="narratives-title">Analyzer Notes</div>
                    <div class="narratives-grid">${cols}</div>
                  </div>`;
        })();

        return `
          <div class="lab-block">
            <div class="lab-block-title">${escapeHtml(testName.toUpperCase())}</div>
            <table class="results-table">
              <thead>
                <tr>
                  <th class="cell-test">Test Name</th>
                  <th class="cell-result">Result</th>
                  <th class="cell-unit">Unit</th>
                  ${showRange ? '<th class="cell-range">Ref. Range</th>' : ''}
                  <th class="cell-status">Status</th>
                  ${showChart ? '<th class="cell-chart">Chart</th>' : ''}
                </tr>
              </thead>
              <tbody>${body}</tbody>
            </table>
            ${narrativesBlock}
            ${histogramsBlock}
            ${interpBlock}
          </div>
        `;
      }).join('')
    ).join('');

  const html = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Lab Report — ${escapeHtml(selectedPatient?.fullName || 'Patient')}</title>
  <!-- Inter is a precision-tuned typeface designed for clinical interfaces;
       it gives the report a polished, modern feel and keeps numerals
       perfectly aligned in the Result column. The system font stack stays
       as a fallback so the report still renders if the CDN is unreachable. -->
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@500&display=swap" rel="stylesheet">
  <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    /* Force the browser to render background colours and gradients on
       PRINT and PDF export — without this Chrome/Edge silently strip the
       blue header band, the status colours and the chart shading. */
    html, body, .page, .header-band, .results-table th, .results-table td,
    .chart-bar, .chart-zone, .chart-marker, .legend-box, .legend-swatch {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
    html, body {
      font-family: 'Inter', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
      font-size: 9.2pt;
      color: #0f172a;
      background: #f3f4f6;
      /* tnum forces tabular-figure spacing so the Result column lines up
         numerically — critical for a clinical document. */
      font-feature-settings: 'kern' 1, 'liga' 1, 'tnum' 1, 'ss01' 1;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      orphans: 3;
      widows: 3;
    }
    .screen-only {
      padding: 16px;
      text-align: center;
      background: #fff;
      border-bottom: 1px solid #e5e7eb;
    }
    .screen-only button {
      padding: 10px 26px; margin: 0 6px;
      border: none; border-radius: 6px;
      background: #144074; color: #ffffff;
      font-weight: 700; font-size: 10.8pt;
      letter-spacing: 0.4px;
      cursor: pointer;
      box-shadow: 0 2px 6px rgba(20,64,116,0.25);
    }
    .screen-only button.close-btn { background: #475569; box-shadow: 0 2px 6px rgba(71,85,105,0.25); }
    .screen-only button:hover { filter: brightness(1.05); }

    .page {
      width: 210mm;
      min-height: 297mm;
      margin: 16px auto;
      padding: 0;
      background: #fff;
      box-shadow: 0 6px 20px rgba(0,0,0,0.08);
      position: relative;
    }

    /* ── Branded header band ──────────────────────────────────────
       Deeper navy gradient for a more professional, clinical look
       (was a lighter mid-blue). A thin accent rule under the band
       seals the design and visually separates the band from the
       patient/order block. */
    .header-band {
      background: linear-gradient(180deg, #1e5593 0%, #144074 100%);
      color: #ffffff;
      padding: 12px 22px 13px 22px;
      display: grid;
      grid-template-columns: 56px 1fr 200px;
      gap: 16px;
      align-items: center;
      border-bottom: 3px solid #d4a017;        /* subtle gold accent rule */
      box-shadow: 0 1px 0 rgba(0,0,0,0.08);
    }
    .header-band .logo {
      width: 52px; height: 52px;
      background: #ffffff;
      border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      overflow: hidden;
      box-shadow: 0 1px 2px rgba(0,0,0,0.18);
      border: 2px solid rgba(255,255,255,0.92);
    }
    .header-band .logo img { width: 100%; height: 100%; object-fit: contain; }
    .header-band .logo-fallback {
      font-weight: 800; color: #144074; font-size: 17pt; letter-spacing: 1px;
    }
    .header-band .facility-name {
      font-size: 16pt;
      font-weight: 800;
      letter-spacing: 0.2px;
      line-height: 1.08;
      text-transform: uppercase;
      text-shadow: 0 1px 0 rgba(0,0,0,0.18);
    }
    .header-band .facility-tag {
      font-size: 9pt;
      font-weight: 600;
      color: #f8d97e;                          /* warm gold-on-navy */
      letter-spacing: 1.2px;
      text-transform: uppercase;
      margin-top: 4px;
    }
    .header-band .facility-meta {
      font-size: 8.4pt;
      color: rgba(255,255,255,0.92);
      margin-top: 6px;
      line-height: 1.45;
      letter-spacing: 0.2px;
    }
    .header-band .barcode-box {
      background: #ffffff;
      padding: 9px 11px 5px 11px;
      border-radius: 5px;
      text-align: center;
      color: #111827;
      box-shadow: 0 1px 2px rgba(0,0,0,0.16);
    }
    .header-band .barcode-box svg {
      width: 100%; height: 44px; display: block;
    }
    .header-band .barcode-fallback {
      font-family: 'Libre Barcode 128', monospace;
      font-size: 10pt; letter-spacing: 1px;
      word-break: break-all;
    }
    .header-band .dates {
      font-size: 8.6pt;
      color: rgba(255,255,255,0.92);
      margin-top: 6px;
      text-align: right;
      letter-spacing: 0.2px;
    }
    .header-band .dates div { margin-top: 3px; }
    .header-band .dates strong {
      color: #ffffff;
      font-weight: 700;
      letter-spacing: 0.4px;
      text-transform: uppercase;
      font-size: 8.2pt;
    }

    /* Report ribbon — sits just under the header band and reinforces
       what this document is, in the same brand voice as the band. */
    .report-ribbon {
      background: #f2c33a;
      color: #144074;
      text-align: center;
      font-weight: 800;
      font-size: 8.8pt;
      letter-spacing: 3px;
      padding: 3px 0 3px 0;
      text-transform: uppercase;
    }

    /* ── Patient + order info block ───────────────────────────── */
    .id-block {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 18px;
      padding: 9px 24px 6px 24px;
      background: #f8fafc;
      border-bottom: 1px solid #e2e8f0;
    }
    .id-block .field {
      font-size: 9pt;
      line-height: 1.4;
      color: #0f172a;
    }
    .id-block .field strong {
      display: inline-block;
      min-width: 120px;
      color: #1f2937;
      font-weight: 700;
      letter-spacing: 0.3px;
      text-transform: uppercase;
      font-size: 7.8pt;
    }
    .id-block .col-right { text-align: left; }
    .id-block .col-right .field strong { min-width: 95px; }

    /* ── Section / test category title ──────────────────────────
       The lab-block wraps a single test category (e.g. "FULL BLOOD
       COUNT"). break-inside:avoid-page is the load-bearing rule
       for the user's pagination requirement: if the next test
       wouldn't fully fit on the current page, browsers move the
       *entire* category to the next page rather than splitting it.
       For a category that's literally too tall for any single page
       (rare — 30+ parameters), the browser falls back to allowing
       a break — and the orphans/widows declarations on the body
       keep at least three rows together on either side. */
    .lab-block {
      padding: 6px 24px 0 24px;
      /* Allow lab sections to split across pages — content flows top-to-
         bottom and fills each page rather than pushing a tall section
         whole to the next page (which leaves blank space at the bottom
         of the previous page). Individual rows still stay intact via
         the .results-table tbody tr rule below. */
    }
    .lab-block-title {
      font-size: 10.5pt;
      font-weight: 800;
      color: #144074;
      text-transform: uppercase;
      letter-spacing: 1.4px;
      text-align: center;
      padding: 8px 0 4px 0;
      position: relative;
      margin-bottom: 4px;
      page-break-after: avoid;
      break-after: avoid;
    }
    .lab-block-title::after {
      content: '';
      display: block;
      width: 56px;
      height: 2px;
      background: #d4a017;
      margin: 4px auto 0 auto;
      border-radius: 2px;
    }

    .histograms-block {
      margin: 10px 0 4px 0;
      padding: 8px 12px 10px 12px;
      border: 1px solid #dbe3ee;
      border-radius: 4px;
      background: #fbfcfe;
      page-break-inside: avoid;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .histograms-title {
      font-size: 8.5pt;
      font-weight: 700;
      color: #144074;
      letter-spacing: 0.6px;
      text-transform: uppercase;
      margin-bottom: 6px;
    }
    .histograms-grid {
      display: flex;
      gap: 10px;
      flex-wrap: wrap;
      align-items: flex-start;
    }
    .histogram-card {
      flex: 1 1 200px;
      min-width: 180px;
      max-width: 260px;
      border: 1px solid #e2e8f0;
      border-radius: 3px;
      padding: 6px 8px 8px 8px;
      background: #ffffff;
      text-align: center;
      page-break-inside: avoid;
    }
    .histogram-name {
      font-size: 8pt;
      font-weight: 700;
      color: #144074;
      margin-bottom: 4px;
      letter-spacing: 0.3px;
    }
    .histogram-img {
      width: 100%;
      height: auto;
      max-height: 160px;
      display: block;
      margin: 0 auto;
      image-rendering: -webkit-optimize-contrast;
    }

    .narratives-block {
      margin: 8px 0 4px 0;
      padding: 6px 10px;
      border: 1px solid #f1d59c;
      border-left: 4px solid #d4a017;
      border-radius: 4px;
      background: #fdf8ee;
      page-break-inside: avoid;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .narratives-title {
      font-size: 8pt;
      font-weight: 700;
      color: #7a5a10;
      letter-spacing: 0.6px;
      text-transform: uppercase;
      margin-bottom: 3px;
    }
    .narratives-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 8px 14px;
    }
    .narrative-col {
      min-width: 0;
      border-left: 1px dashed #e5d3a3;
      padding-left: 8px;
    }
    .narrative-col:first-child {
      border-left: none;
      padding-left: 0;
    }
    .narrative-col-title {
      font-size: 8pt;
      font-weight: 800;
      color: #7a5a10;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      margin-bottom: 2px;
    }
    .narrative-list {
      margin: 0;
      padding-left: 14px;
      font-size: 8.5pt;
      color: #2c3e50;
      line-height: 1.45;
    }
    .narrative-list li {
      margin: 0;
      padding: 0;
    }

    .interpretation-block {
      margin: 10px 0 4px 0;
      padding: 8px 10px;
      border: 1px solid #c5cde4;
      border-radius: 4px;
      font-size: 8pt;
      color: #2d3748;
      background: #f7f9fc;
      page-break-inside: avoid;
    }
    .interpretation-block p {
      margin: 0 0 6px 0;
      font-size: 8.5pt;
      color: #0f172a;
      line-height: 1.5;
    }
    .interpretation-block table {
      width: 100%;
      border-collapse: collapse;
      font-size: 7.5pt;
    }
    .interpretation-block table th {
      background: #144074;
      color: #ffffff;
      font-weight: 700;
      border: 1px solid #0d2a56;
      padding: 4px 6px;
      text-align: center;
    }
    .interpretation-block table th:first-child {
      text-align: left;
    }
    .interpretation-block table td {
      border: 1px solid #aab4c8;
      padding: 3px 6px;
      text-align: center;
    }
    .interpretation-block table td:first-child {
      text-align: left;
    }

    /* ── Results table ──────────────────────────────────────────
       display:table-header-group on the thead tells the browser to
       repeat the column header row at the top of each new page when
       a very-large table is split — so a 40-parameter FBC that spills
       to a second page still shows "Test Name / Result / Unit / Ref.
       Range / Status / Chart" at the top of page 2. */
    .results-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 8.6pt;
      /* Tables can split mid-body if they don't fit on the current page.
         The thead repeats at the top of every page (table-header-group)
         and individual rows stay intact (tbody tr avoid). This fills
         the available space before flowing onto the next page. */
    }
    .results-table thead { display: table-header-group; }
    .results-table tfoot { display: table-row-group; }
    .results-table tbody tr {
      page-break-inside: avoid;
      break-inside: avoid;
    }
    .results-table th {
      background: linear-gradient(180deg, #1e5593 0%, #144074 100%);
      color: #ffffff;
      text-align: left;
      font-weight: 700;
      padding: 4px 8px;
      border-bottom: 2px solid #d4a017;
      font-size: 7.6pt;
      letter-spacing: 0.6px;
      text-transform: uppercase;
    }
    .results-table td {
      padding: 3.5px 8px;
      border-bottom: 1px solid #e5e7eb;
      vertical-align: middle;
      color: #0f172a;
      line-height: 1.3;
    }
    .results-table tbody tr:nth-child(even) td { background: #f8fafc; }
    /* Test name stays at normal weight per the design brief — bold only
       on Result and Status, which is what the reader's eye scans for. */
    /* Column widths re-balanced: test names are the longest values in the
       table (e.g. "Mean Corpuscular Haemoglobin Concentration"), so they
       get the biggest share. The chart is purely a visual sanity-check so
       a narrower lane is plenty — the marker and zone still scale to fit. */
    .cell-test   { width: 30%; font-weight: 500; color: #1f2937; }
    .cell-result { width: 12%; font-weight: 700; font-size: 9pt; color: #144074; font-variant-numeric: tabular-nums; }
    .cell-unit   { width: 9%;  color: #475569; font-size: 8.4pt; }
    .cell-range  { width: 13%; color: #475569; font-variant-numeric: tabular-nums; font-size: 8.4pt; }
    .cell-status { width: 10%; font-weight: 700; text-align: left; letter-spacing: 0.4px; }
    .cell-chart  { width: 26%; }
    /* Status text and the chart-marker triangle now share a single palette
       so the reader can match the colour in the Status column to the
       triangle on the chart without colour shift. */
    .status-normal   { color: #16a34a; }
    .status-high     { color: #dc2626; }
    .status-low      { color: #d97706; }
    .status-other    { color: #4b5563; }

    /* ── Per-row chart bar ──────────────────────────────────────
       The bar's full width represents a domain wide enough to contain
       both the reference range and the patient's value. The shaded green
       zone marks the normal range; a coloured triangle plots the value. */
    .chart-cell { padding: 2px 2px 9px 2px; min-width: 105px; }
    .chart-bar {
      position: relative;
      width: 100%;
      height: 6px;
      background: #f1f5f9;
      border: 1px solid #e2e8f0;
      border-radius: 3px;
    }
    .chart-zone {
      position: absolute; top: 0; bottom: 0;
      background: #bbf7d0;
      border-left: 1px solid #16a34a;
      border-right: 1px solid #16a34a;
    }
    .chart-marker {
      position: absolute;
      top: -7px;
      transform: translateX(-50%);
      font-size: 9.5pt;
      line-height: 1;
      pointer-events: none;
    }
    .chart-marker.status-normal { color: #16a34a; }
    .chart-marker.status-high   { color: #dc2626; }
    .chart-marker.status-low    { color: #d97706; }
    .chart-marker.status-other  { color: #4b5563; }
    .chart-val {
      position: absolute;
      top: 8px;
      transform: translateX(-50%);
      font-size: 7.2pt;
      font-weight: 700;
      white-space: nowrap;
      font-variant-numeric: tabular-nums;
    }
    .chart-val.status-normal { color: #16a34a; }
    .chart-val.status-high   { color: #dc2626; }
    .chart-val.status-low    { color: #d97706; }
    .chart-val.status-other  { color: #4b5563; }
    .chart-na { color: #9ca3af; font-style: italic; font-size: 9pt; }

    /* ── Colour-key legend ───────────────────────────────────────
       Kept to a single line so it doesn't steal vertical space from the
       result tables. flex-wrap:nowrap is the load-bearing rule — at
       this font size the six items fit comfortably on a 210 mm sheet. */
    .legend-box {
      margin: 4px 24px 2px 24px;
      padding: 3px 10px;
      border: 1px solid #cbd5e1;
      border-radius: 4px;
      background: #f8fafc;
      display: flex;
      flex-wrap: nowrap;
      gap: 10px;
      align-items: center;
      font-size: 7.2pt;
      white-space: nowrap;
      overflow: hidden;
    }
    .legend-title {
      font-weight: 800; color: #144074;
      letter-spacing: 0.6px; text-transform: uppercase;
      font-size: 7.4pt;
      margin-right: 2px;
      flex-shrink: 0;
    }
    .legend-item {
      display: inline-flex; align-items: center; gap: 4px;
      flex-shrink: 0;
    }
    .legend-swatch {
      display: inline-block;
      width: 16px; height: 8px;
      border-radius: 2px;
      border: 1px solid #94a3b8;
    }
    .legend-swatch.swatch-zone {
      background: #bbf7d0; border-color: #16a34a;
    }
    .legend-swatch.swatch-out {
      background: #f1f5f9; border-color: #e2e8f0;
    }
    .legend-tri {
      display: inline-block;
      font-size: 9pt; line-height: 1;
      transform: translateY(-1px);
    }
    .legend-tri.t-normal { color: #16a34a; }
    .legend-tri.t-low    { color: #d97706; }
    .legend-tri.t-high   { color: #dc2626; }

    /* ── End of report marker ────────────────────────────────── */
    .end-of-report {
      text-align: center;
      font-size: 8.6pt;
      color: #144074;
      margin: 14px 0 10px 0;
      letter-spacing: 4px;
      font-weight: 700;
      text-transform: uppercase;
    }

    /* ── Signature grid ──────────────────────────────────────── */
    .sig-block {
      padding: 0 24px 10px 24px;
      page-break-inside: avoid;
      break-inside: avoid;
    }
    .sig-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 8.8pt;
    }
    .sig-table th {
      text-align: left;
      font-weight: 800;
      color: #144074;
      padding: 5px 7px 4px 7px;
      border-bottom: 2px solid #144074;
      font-size: 7.8pt;
      letter-spacing: 0.6px;
      text-transform: uppercase;
    }
    .sig-table th.sig-line,
    .sig-table th.date-col { text-align: left; }
    .sig-table td {
      padding: 13px 7px 5px 7px;
      border-bottom: 1px solid #94a3b8;
      vertical-align: top;
      font-size: 8.6pt;
      line-height: 1.35;
    }
    .sig-table td.role-cell { font-weight: 800; color: #144074; }
    .sig-table td.name-cell { color: #111827; font-weight: 600; }
    .sig-table td .title-line {
      display: block;
      color: #6b7280;
      font-size: 7.8pt;
      margin-top: 2px;
      font-weight: 500;
      letter-spacing: 0.2px;
    }

    /* ── Footnote ─────────────────────────────────────────────── */
    .footnote {
      padding: 7px 24px 14px 24px;
      font-size: 7.8pt;
      color: #475569;
      border-top: 2px solid #144074;
      line-height: 1.4;
    }
    .footnote strong {
      color: #144074;
      font-weight: 800;
      letter-spacing: 0.4px;
      text-transform: uppercase;
      font-size: 7.4pt;
    }

    @media print {
      /* Belt-and-braces: repeat the color-adjust hint inside the print
         block too. Some browsers honour the @media print rule even when
         they ignore the top-level one. */
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
      html, body { background: #fff; }
      .screen-only { display: none !important; }
      .page {
        margin: 0; box-shadow: none;
        width: 210mm; min-height: 297mm;
        page-break-after: always;
      }
      /* The gradient sometimes drops in print — keep a solid fallback
         so the band always carries colour even if the gradient is
         dropped by the print engine. */
      .header-band {
        background: #144074 !important;
        background-image: linear-gradient(180deg, #1e5593 0%, #144074 100%) !important;
        color: #ffffff !important;
        border-bottom: 3px solid #d4a017 !important;
      }
      .report-ribbon { background: #f2c33a !important; color: #144074 !important; }
      .results-table th {
        background: #144074 !important;
        background-image: linear-gradient(180deg, #1e5593 0%, #144074 100%) !important;
        color: #ffffff !important;
      }
      .chart-zone { background: #bbf7d0 !important; }
      /* Keep the analyzer's distribution graphs together — never split a
         histogram card across a page break. */
      .histograms-block,
      .histogram-card { page-break-inside: avoid; }
      /* Pagination contract:
         •  Each .lab-block (one test category) stays together. If it
            doesn't fit on the remaining space, the whole category moves
            to the next page rather than being split mid-table.
         •  Individual rows never split across a page boundary.
         •  The column header row repeats at the top of every page when a
            very large category does spill over (display:table-header-group
            in the screen rules carries through to print).
         •  The signature block sticks together so the three roles line
            up under one continuous rule. */
      /* Keep small atomic blocks together (legend, patient-id, signature,
         interpretation block) — but let lab tables flow freely so the
         pages fill top-to-bottom without leaving blanks. */
      .sig-block, .legend-box, .id-block, .interpretation-block {
        page-break-inside: avoid !important;
        break-inside: avoid-page !important;
      }
      .lab-block-title {
        /* Don't strand a section title at the very bottom of a page —
           if the title would print alone, push it to the next page so it
           sits with its rows. */
        page-break-after: avoid !important;
        break-after: avoid !important;
      }
      .results-table tbody tr {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }
      .results-table thead { display: table-header-group; }
    }
  </style>
</head>
<body>
  <div class="screen-only">
    <button onclick="window.print()">Print / Save as PDF</button>
    <button class="close-btn" onclick="window.close()">Close</button>
  </div>

  <div class="page">
    <!-- Blue header band -->
    <div class="header-band">
      <div class="logo">
        ${company.logo
          ? `<img src="${escapeHtml(company.logo)}" alt="logo" />`
          : `<div class="logo-fallback">${escapeHtml((company.name || 'L')[0])}</div>`}
      </div>
      <div>
        <div class="facility-name">${escapeHtml(company.name || 'Facility Name')}</div>
        ${company.mmda ? `<div class="facility-tag">${escapeHtml(company.mmda)}</div>` : ''}
        <div class="facility-meta">
          ${facilityAddress ? escapeHtml(facilityAddress) : ''}<br/>
          ${company.phone ? '☎ ' + escapeHtml(company.phone) : ''}${company.phone && company.email ? ' · ' : ''}${company.email ? '✉ ' + escapeHtml(company.email) : ''}
        </div>
      </div>
      <div>
        <div class="barcode-box">
          <svg id="lab-barcode"></svg>
          <div id="barcode-fallback" class="barcode-fallback" style="display:none;">${escapeHtml(barcodeValue)}</div>
        </div>
        <div class="dates">
          <div><strong>Registered:</strong> ${escapeHtml(registeredOn)}</div>
          <div><strong>Reported:</strong> ${escapeHtml(reportedOn)}</div>
        </div>
      </div>
    </div>

    <!-- Brand ribbon — reinforces what this document is. -->
    <div class="report-ribbon">· Laboratory Investigation Report ·</div>

    <!-- Patient + order info -->
    <div class="id-block">
      <div class="col-left">
        <div class="field"><strong>Patient name:</strong> ${escapeHtml(selectedPatient?.fullName || '-')}</div>
        <div class="field"><strong>Age:</strong> ${escapeHtml(selectedPatient?.age || '-')}</div>
        <div class="field"><strong>Sex:</strong> ${escapeHtml(selectedPatient?.gender || '-')}</div>
        <div class="field"><strong>Referring Doctor:</strong> ${escapeHtml(signatures.referringDoctor || selectedPatient?.referring_doctor || '-')}</div>
      </div>
      <div class="col-right">
        <div class="field"><strong>Order number:</strong> ${escapeHtml(orderNumber)}</div>
        <div class="field"><strong>Phone:</strong> ${escapeHtml(selectedPatient?.tel || selectedPatient?.phone || company.phone || '-')}</div>
        <div class="field"><strong>Email:</strong> ${escapeHtml(selectedPatient?.email || company.email || '-')}</div>
        <div class="field"><strong>Address:</strong> ${escapeHtml(selectedPatient?.address || facilityAddress || '-')}</div>
        <div class="field"><strong>Patient ID:</strong> ${escapeHtml(selectedPatient?.id || '-')}</div>
      </div>
    </div>

    <!-- Colour key legend — single-line; labels trimmed so they all
         fit on a 210 mm sheet at the reduced font size. -->
    <div class="legend-box">
      <span class="legend-title">Chart Key</span>
      <span class="legend-item"><span class="legend-swatch swatch-zone"></span>Normal range</span>
      <span class="legend-item"><span class="legend-swatch swatch-out"></span>Out of range</span>
      <span class="legend-item"><span class="legend-tri t-normal" style="${colorStyle('#16a34a')}">▲</span>Normal</span>
      <span class="legend-item"><span class="legend-tri t-low"    style="${colorStyle('#d97706')}">▲</span>Low</span>
      <span class="legend-item"><span class="legend-tri t-high"   style="${colorStyle('#dc2626')}">▲</span>High</span>
    </div>

    <!-- Result tables -->
    ${labsHTML}

    <!-- End of report -->
    <div class="end-of-report">***** End of Report *****</div>

    <!-- Signature block -->
    <div class="sig-block">
      <table class="sig-table">
        <thead>
          <tr>
            <th style="width:22%;">Role</th>
            <th style="width:28%;">Name</th>
            <th class="sig-line" style="width:30%;">Signature</th>
            <th class="date-col" style="width:20%;">Date</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="role-cell">Tested by:<br/><span class="title-line">Medical Laboratory Scientist</span></td>
            <td class="name-cell">${escapeHtml(testedBy || '-')}</td>
            <td></td>
            <td>${escapeHtml(todayStr)}</td>
          </tr>
          <tr>
            <td class="role-cell">Verified by:<br/><span class="title-line">${escapeHtml(verifiedTitle || 'Senior Medical Laboratory Scientist')}</span></td>
            <td class="name-cell">${escapeHtml(verifiedBy || '-')}</td>
            <td></td>
            <td>${verifiedBy ? escapeHtml(todayStr) : ''}</td>
          </tr>
          <tr>
            <td class="role-cell">Approved by:<br/><span class="title-line">${escapeHtml(approvedTitle || 'Senior Medical Laboratory Scientist')}</span></td>
            <td class="name-cell">${escapeHtml(approvedBy || '-')}</td>
            <td></td>
            <td>${approvedBy ? escapeHtml(todayStr) : ''}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="footnote">
      <strong>Important Notes —</strong>
      Results relate only to the specimen tested at the time of analysis. Clinical correlation is recommended.
      This report is confidential and intended solely for the named patient and the referring clinician.
      Unauthorized reproduction or distribution is prohibited.
      <strong>&nbsp;·&nbsp;For Medical Use Only</strong>
    </div>
  </div>

  <script>
    // Render the barcode once JsBarcode finishes loading. If the CDN is
    // unreachable (offline print), fall back to a textual representation so
    // the box isn't empty.
    (function () {
      function renderBarcode() {
        try {
          if (typeof JsBarcode !== 'function') throw new Error('no jsbarcode');
          JsBarcode('#lab-barcode', ${JSON.stringify(barcodeValue)}, {
            format: 'CODE128',
            displayValue: true,
            fontSize: 11,
            height: 38,
            margin: 0
          });
        } catch (e) {
          var svg = document.getElementById('lab-barcode');
          var fb  = document.getElementById('barcode-fallback');
          if (svg) svg.style.display = 'none';
          if (fb)  fb.style.display  = 'block';
        }
      }
      if (document.readyState === 'complete' || document.readyState === 'interactive') {
        setTimeout(renderBarcode, 0);
      } else {
        document.addEventListener('DOMContentLoaded', renderBarcode);
      }
    })();
  </script>
</body>
</html>`;

  return html;
};
